import type {
  ParsedProduct,
  ParsedRawMaterial,
  ParsedModifier,
  ParsedCategory,
  ParsedImportData,
} from './parser';

export interface ValidationError {
  row: number;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidatedProduct extends ParsedProduct {
  errors: ValidationError[];
  isValid: boolean;
  action: 'create' | 'update';
  existingId?: string;
}

export interface ValidatedRawMaterial extends ParsedRawMaterial {
  errors: ValidationError[];
  isValid: boolean;
  action: 'create' | 'update';
  existingId?: string;
  resolvedSupplierId?: string;
}

export interface ValidatedModifier extends ParsedModifier {
  errors: ValidationError[];
  isValid: boolean;
}

export interface ValidatedCategory extends ParsedCategory {
  errors: ValidationError[];
  isValid: boolean;
  action: 'create' | 'update';
  existingId?: string;
}

export interface ValidationContext {
  existingProducts: Map<string, { id: string; name: string }>;
  existingCategories: Map<string, { id: string; name: string }>;
  existingSuppliers: Map<string, { id: string; name: string }>;
  existingRawMaterials: Map<string, { id: string; name: string }>;
  existingModifierGroups: Map<string, { id: string; name: string }>;
  organizationId: string;
  locationId: string;
}

export interface ValidationResult {
  products: ValidatedProduct[];
  rawMaterials: ValidatedRawMaterial[];
  modifiers: ValidatedModifier[];
  categories: ValidatedCategory[];
  summary: {
    products: { valid: number; errors: number; toCreate: number; toUpdate: number };
    rawMaterials: { valid: number; errors: number; toCreate: number; toUpdate: number };
    modifiers: { valid: number; errors: number };
    categories: { valid: number; errors: number; toCreate: number; toUpdate: number };
    totalErrors: number;
    totalWarnings: number;
  };
}

// Valid units of measure for raw materials
export const VALID_UNITS_OF_MEASURE = [
  'each', 'kg', 'g', 'lb', 'oz', 'liter', 'ml', 'gallon', 'case', 'box', 'bag', 'roll'
];

// Valid categories for raw materials
export const VALID_RAW_MATERIAL_CATEGORIES = [
  'raw_ingredient', 'packaging', 'supplies', 'cleaning', 'other'
];

// Validate a single product
function validateProduct(
  product: ParsedProduct,
  context: ValidationContext,
  seenSkus: Set<string>
): ValidatedProduct {
  const errors: ValidationError[] = [];

  // Required field: name
  if (!product.name || product.name.trim() === '') {
    errors.push({
      row: product.row,
      field: 'name',
      message: 'Product name is required',
      severity: 'error',
    });
  } else if (product.name.length > 255) {
    errors.push({
      row: product.row,
      field: 'name',
      message: 'Product name must be 255 characters or less',
      severity: 'error',
    });
  }

  // Required field: SKU (for matching)
  if (!product.sku || product.sku.trim() === '') {
    errors.push({
      row: product.row,
      field: 'sku',
      message: 'SKU is required for import (used to match existing products)',
      severity: 'error',
    });
  } else {
    // Check for duplicate SKU in import file
    const normalizedSku = product.sku.toLowerCase().trim();
    if (seenSkus.has(normalizedSku)) {
      errors.push({
        row: product.row,
        field: 'sku',
        message: `Duplicate SKU "${product.sku}" found in import file`,
        severity: 'error',
      });
    } else {
      seenSkus.add(normalizedSku);
    }
  }

  // Required field: price
  if (product.price === undefined || product.price === null) {
    errors.push({
      row: product.row,
      field: 'price',
      message: 'Price is required',
      severity: 'error',
    });
  } else if (product.price < 0) {
    errors.push({
      row: product.row,
      field: 'price',
      message: 'Price cannot be negative',
      severity: 'error',
    });
  }

  // Optional field validation: cost
  if (product.cost !== undefined && product.cost < 0) {
    errors.push({
      row: product.row,
      field: 'cost',
      message: 'Cost cannot be negative',
      severity: 'error',
    });
  }

  // Optional field validation: tax_rate
  if (product.tax_rate !== undefined) {
    if (product.tax_rate < 0 || product.tax_rate > 100) {
      errors.push({
        row: product.row,
        field: 'tax_rate',
        message: 'Tax rate must be between 0 and 100',
        severity: 'error',
      });
    }
  }

  // Optional field validation: low_stock_threshold
  if (product.low_stock_threshold !== undefined && product.low_stock_threshold < 0) {
    errors.push({
      row: product.row,
      field: 'low_stock_threshold',
      message: 'Low stock threshold cannot be negative',
      severity: 'error',
    });
  }

  // Optional field validation: par_level
  if (product.par_level !== undefined && product.par_level < 0) {
    errors.push({
      row: product.row,
      field: 'par_level',
      message: 'Par level cannot be negative',
      severity: 'error',
    });
  }

  // Optional field validation: initial_stock
  if (product.initial_stock !== undefined && product.initial_stock < 0) {
    errors.push({
      row: product.row,
      field: 'initial_stock',
      message: 'Initial stock cannot be negative',
      severity: 'error',
    });
  }

  // Check if product exists (for update vs create)
  let action: 'create' | 'update' = 'create';
  let existingId: string | undefined;

  if (product.sku) {
    const normalizedSku = product.sku.toLowerCase().trim();
    const existing = context.existingProducts.get(normalizedSku);
    if (existing) {
      action = 'update';
      existingId = existing.id;
      errors.push({
        row: product.row,
        field: 'sku',
        message: `Product with SKU "${product.sku}" exists and will be updated`,
        severity: 'warning',
      });
    }
  }

  // Category warning (will be created if missing)
  if (product.category) {
    const normalizedCategory = product.category.toLowerCase().trim();
    if (!context.existingCategories.has(normalizedCategory)) {
      errors.push({
        row: product.row,
        field: 'category',
        message: `Category "${product.category}" does not exist and will be created`,
        severity: 'warning',
      });
    }
  }

  const hasErrors = errors.some(e => e.severity === 'error');

  return {
    ...product,
    errors,
    isValid: !hasErrors,
    action,
    existingId,
  };
}

// Validate a single raw material
function validateRawMaterial(
  material: ParsedRawMaterial,
  context: ValidationContext,
  seenSkus: Set<string>
): ValidatedRawMaterial {
  const errors: ValidationError[] = [];

  // Required field: name
  if (!material.name || material.name.trim() === '') {
    errors.push({
      row: material.row,
      field: 'name',
      message: 'Item name is required',
      severity: 'error',
    });
  } else if (material.name.length > 255) {
    errors.push({
      row: material.row,
      field: 'name',
      message: 'Item name must be 255 characters or less',
      severity: 'error',
    });
  }

  // Required field: unit_of_measure
  if (!material.unit_of_measure || material.unit_of_measure.trim() === '') {
    errors.push({
      row: material.row,
      field: 'unit_of_measure',
      message: 'Unit of measure is required',
      severity: 'error',
    });
  } else {
    const normalizedUnit = material.unit_of_measure.toLowerCase().trim();
    if (!VALID_UNITS_OF_MEASURE.includes(normalizedUnit)) {
      errors.push({
        row: material.row,
        field: 'unit_of_measure',
        message: `Invalid unit of measure. Valid options: ${VALID_UNITS_OF_MEASURE.join(', ')}`,
        severity: 'error',
      });
    }
  }

  // Optional: SKU uniqueness
  if (material.sku) {
    const normalizedSku = material.sku.toLowerCase().trim();
    if (seenSkus.has(normalizedSku)) {
      errors.push({
        row: material.row,
        field: 'sku',
        message: `Duplicate SKU "${material.sku}" found in import file`,
        severity: 'error',
      });
    } else {
      seenSkus.add(normalizedSku);
    }
  }

  // Optional: cost validation
  if (material.cost_per_unit !== undefined && material.cost_per_unit < 0) {
    errors.push({
      row: material.row,
      field: 'cost_per_unit',
      message: 'Cost per unit cannot be negative',
      severity: 'error',
    });
  }

  // Optional: category validation
  if (material.category) {
    const normalizedCategory = material.category.toLowerCase().trim();
    if (!VALID_RAW_MATERIAL_CATEGORIES.includes(normalizedCategory)) {
      errors.push({
        row: material.row,
        field: 'category',
        message: `Invalid category. Valid options: ${VALID_RAW_MATERIAL_CATEGORIES.join(', ')}`,
        severity: 'error',
      });
    }
  }

  // Optional: reorder_point validation
  if (material.reorder_point !== undefined && material.reorder_point < 0) {
    errors.push({
      row: material.row,
      field: 'reorder_point',
      message: 'Reorder point cannot be negative',
      severity: 'error',
    });
  }

  // Optional: reorder_quantity validation
  if (material.reorder_quantity !== undefined && material.reorder_quantity < 0) {
    errors.push({
      row: material.row,
      field: 'reorder_quantity',
      message: 'Reorder quantity cannot be negative',
      severity: 'error',
    });
  }

  // Optional: initial_stock validation
  if (material.initial_stock !== undefined && material.initial_stock < 0) {
    errors.push({
      row: material.row,
      field: 'initial_stock',
      message: 'Initial stock cannot be negative',
      severity: 'error',
    });
  }

  // Check if material exists (by SKU)
  let action: 'create' | 'update' = 'create';
  let existingId: string | undefined;

  if (material.sku) {
    const normalizedSku = material.sku.toLowerCase().trim();
    const existing = context.existingRawMaterials.get(normalizedSku);
    if (existing) {
      action = 'update';
      existingId = existing.id;
      errors.push({
        row: material.row,
        field: 'sku',
        message: `Raw material with SKU "${material.sku}" exists and will be updated`,
        severity: 'warning',
      });
    }
  }

  // Supplier lookup
  let resolvedSupplierId: string | undefined;
  if (material.supplier_name) {
    const normalizedSupplier = material.supplier_name.toLowerCase().trim();
    const supplier = context.existingSuppliers.get(normalizedSupplier);
    if (supplier) {
      resolvedSupplierId = supplier.id;
    } else {
      errors.push({
        row: material.row,
        field: 'supplier_name',
        message: `Supplier "${material.supplier_name}" not found. Item will be imported without supplier.`,
        severity: 'warning',
      });
    }
  }

  const hasErrors = errors.some(e => e.severity === 'error');

  return {
    ...material,
    errors,
    isValid: !hasErrors,
    action,
    existingId,
    resolvedSupplierId,
  };
}

// Validate a single modifier
function validateModifier(modifier: ParsedModifier): ValidatedModifier {
  const errors: ValidationError[] = [];

  // Required field: group_name
  if (!modifier.group_name || modifier.group_name.trim() === '') {
    errors.push({
      row: modifier.row,
      field: 'group_name',
      message: 'Modifier group name is required',
      severity: 'error',
    });
  }

  // Required field: option_name
  if (!modifier.option_name || modifier.option_name.trim() === '') {
    errors.push({
      row: modifier.row,
      field: 'option_name',
      message: 'Option name is required',
      severity: 'error',
    });
  }

  // Validate min/max selections
  if (modifier.min_selections !== undefined && modifier.min_selections < 0) {
    errors.push({
      row: modifier.row,
      field: 'min_selections',
      message: 'Minimum selections cannot be negative',
      severity: 'error',
    });
  }

  if (modifier.max_selections !== undefined && modifier.max_selections < 0) {
    errors.push({
      row: modifier.row,
      field: 'max_selections',
      message: 'Maximum selections cannot be negative',
      severity: 'error',
    });
  }

  if (
    modifier.min_selections !== undefined &&
    modifier.max_selections !== undefined &&
    modifier.min_selections > modifier.max_selections
  ) {
    errors.push({
      row: modifier.row,
      field: 'min_selections',
      message: 'Minimum selections cannot be greater than maximum selections',
      severity: 'error',
    });
  }

  const hasErrors = errors.some(e => e.severity === 'error');

  return {
    ...modifier,
    errors,
    isValid: !hasErrors,
  };
}

// Validate a single category
function validateCategory(
  category: ParsedCategory,
  context: ValidationContext,
  seenNames: Set<string>
): ValidatedCategory {
  const errors: ValidationError[] = [];

  // Required field: name
  if (!category.name || category.name.trim() === '') {
    errors.push({
      row: category.row,
      field: 'name',
      message: 'Category name is required',
      severity: 'error',
    });
  } else {
    // Check for duplicate name in import file
    const normalizedName = category.name.toLowerCase().trim();
    if (seenNames.has(normalizedName)) {
      errors.push({
        row: category.row,
        field: 'name',
        message: `Duplicate category name "${category.name}" found in import file`,
        severity: 'error',
      });
    } else {
      seenNames.add(normalizedName);
    }
  }

  // Check if category exists
  let action: 'create' | 'update' = 'create';
  let existingId: string | undefined;

  if (category.name) {
    const normalizedName = category.name.toLowerCase().trim();
    const existing = context.existingCategories.get(normalizedName);
    if (existing) {
      action = 'update';
      existingId = existing.id;
      errors.push({
        row: category.row,
        field: 'name',
        message: `Category "${category.name}" exists and will be updated`,
        severity: 'warning',
      });
    }
  }

  // Parent category warning
  if (category.parent_category) {
    const normalizedParent = category.parent_category.toLowerCase().trim();
    if (!context.existingCategories.has(normalizedParent) && !seenNames.has(normalizedParent)) {
      errors.push({
        row: category.row,
        field: 'parent_category',
        message: `Parent category "${category.parent_category}" not found`,
        severity: 'warning',
      });
    }
  }

  const hasErrors = errors.some(e => e.severity === 'error');

  return {
    ...category,
    errors,
    isValid: !hasErrors,
    action,
    existingId,
  };
}

// Main validation function
export function validateImportData(
  data: ParsedImportData,
  context: ValidationContext
): ValidationResult {
  const productSkus = new Set<string>();
  const materialSkus = new Set<string>();
  const categoryNames = new Set<string>();

  // Validate products
  const products = data.products.map(p => validateProduct(p, context, productSkus));

  // Validate raw materials
  const rawMaterials = data.rawMaterials.map(m => validateRawMaterial(m, context, materialSkus));

  // Validate modifiers
  const modifiers = data.modifiers.map(m => validateModifier(m));

  // Validate categories
  const categories = data.categories.map(c => validateCategory(c, context, categoryNames));

  // Calculate summary
  const productErrors = products.filter(p => !p.isValid).length;
  const productValid = products.length - productErrors;
  const productCreate = products.filter(p => p.isValid && p.action === 'create').length;
  const productUpdate = products.filter(p => p.isValid && p.action === 'update').length;

  const materialErrors = rawMaterials.filter(m => !m.isValid).length;
  const materialValid = rawMaterials.length - materialErrors;
  const materialCreate = rawMaterials.filter(m => m.isValid && m.action === 'create').length;
  const materialUpdate = rawMaterials.filter(m => m.isValid && m.action === 'update').length;

  const modifierErrors = modifiers.filter(m => !m.isValid).length;
  const modifierValid = modifiers.length - modifierErrors;

  const categoryErrors = categories.filter(c => !c.isValid).length;
  const categoryValid = categories.length - categoryErrors;
  const categoryCreate = categories.filter(c => c.isValid && c.action === 'create').length;
  const categoryUpdate = categories.filter(c => c.isValid && c.action === 'update').length;

  const totalErrors = [
    ...products.flatMap(p => p.errors),
    ...rawMaterials.flatMap(m => m.errors),
    ...modifiers.flatMap(m => m.errors),
    ...categories.flatMap(c => c.errors),
  ].filter(e => e.severity === 'error').length;

  const totalWarnings = [
    ...products.flatMap(p => p.errors),
    ...rawMaterials.flatMap(m => m.errors),
    ...modifiers.flatMap(m => m.errors),
    ...categories.flatMap(c => c.errors),
  ].filter(e => e.severity === 'warning').length;

  return {
    products,
    rawMaterials,
    modifiers,
    categories,
    summary: {
      products: {
        valid: productValid,
        errors: productErrors,
        toCreate: productCreate,
        toUpdate: productUpdate,
      },
      rawMaterials: {
        valid: materialValid,
        errors: materialErrors,
        toCreate: materialCreate,
        toUpdate: materialUpdate,
      },
      modifiers: {
        valid: modifierValid,
        errors: modifierErrors,
      },
      categories: {
        valid: categoryValid,
        errors: categoryErrors,
        toCreate: categoryCreate,
        toUpdate: categoryUpdate,
      },
      totalErrors,
      totalWarnings,
    },
  };
}

// Create validation context from existing data
export function createValidationContext(
  existingProducts: Array<{ id: string; name: string; sku: string | null }>,
  existingCategories: Array<{ id: string; name: string }>,
  existingSuppliers: Array<{ id: string; name: string }>,
  existingRawMaterials: Array<{ id: string; name: string; sku: string | null }>,
  existingModifierGroups: Array<{ id: string; name: string }>,
  organizationId: string,
  locationId: string
): ValidationContext {
  // Build lookup maps with lowercase keys
  const productMap = new Map<string, { id: string; name: string }>();
  existingProducts.forEach(p => {
    if (p.sku) {
      productMap.set(p.sku.toLowerCase().trim(), { id: p.id, name: p.name });
    }
  });

  const categoryMap = new Map<string, { id: string; name: string }>();
  existingCategories.forEach(c => {
    categoryMap.set(c.name.toLowerCase().trim(), { id: c.id, name: c.name });
  });

  const supplierMap = new Map<string, { id: string; name: string }>();
  existingSuppliers.forEach(s => {
    supplierMap.set(s.name.toLowerCase().trim(), { id: s.id, name: s.name });
  });

  const rawMaterialMap = new Map<string, { id: string; name: string }>();
  existingRawMaterials.forEach(m => {
    if (m.sku) {
      rawMaterialMap.set(m.sku.toLowerCase().trim(), { id: m.id, name: m.name });
    }
  });

  const modifierGroupMap = new Map<string, { id: string; name: string }>();
  existingModifierGroups.forEach(g => {
    modifierGroupMap.set(g.name.toLowerCase().trim(), { id: g.id, name: g.name });
  });

  return {
    existingProducts: productMap,
    existingCategories: categoryMap,
    existingSuppliers: supplierMap,
    existingRawMaterials: rawMaterialMap,
    existingModifierGroups: modifierGroupMap,
    organizationId,
    locationId,
  };
}
